import React, { useState, useEffect } from "react";
import { questionsData } from "./questionData";
import "./ChecklistPage.css";
const SQFT_TO_SQM = 0.092903;
const climateZoneRanges = {
  Composite: [
    { min: 376, max: 400, points: 2 },
    { min: 351, max: 375, points: 4 },
    { min: 326, max: 350, points: 6 },
    { min: 301, max: 325, points: 8 },
    { min: 276, max: 300, points: 10 },
    { min: 251, max: 275, points: 12 },
    { min: 0, max: 250, points: 14 },
  ],
  "Warm & Humid": [
    { min: 386, max: 410, points: 2 },
    { min: 361, max: 385, points: 4 },
    { min: 336, max: 360, points: 6 },
    { min: 311, max: 335, points: 8 },
    { min: 286, max: 310, points: 10 },
    { min: 261, max: 285, points: 12 },
    { min: 0, max: 260, points: 14 },
  ],
  "Hot & Dry": [
    { min: 241, max: 265, points: 2 },
    { min: 216, max: 240, points: 4 },
    { min: 191, max: 215, points: 6 },
    { min: 166, max: 190, points: 8 },
    { min: 141, max: 165, points: 10 },
    { min: 116, max: 140, points: 12 },
    { min: 0, max: 115, points: 14 },
  ],
  Temperate: [
    { min: 326, max: 350, points: 2 },
    { min: 301, max: 325, points: 4 },
    { min: 276, max: 300, points: 6 },
    { min: 251, max: 275, points: 8 },
    { min: 226, max: 250, points: 10 },
    { min: 201, max: 225, points: 12 },
    { min: 0, max: 200, points: 14 },
  ],
};

const mandatoryQuestions = {
  1: [0, 1, 2], // First 3 questions in Site & Management
  2: [0, 1], // First 2 questions in Water efficiency
  3: [0], // First 2 questions in Energy efficiency
  4: [0, 1], // First 2 questions in Health & Comfort
};

const areMandatoryQuestionsAnswered = (selectedAnswers) => {
  for (const [categoryId, questionIndices] of Object.entries(mandatoryQuestions)) {
    for (const index of questionIndices) {
      const key = `${categoryId}-${index}`;
      if (selectedAnswers[key] !== true) {
        return false;
      }
    }
  }
  return true;
};

const areAllQuestionsAnswered = (selectedAnswers) => {
  // Check if all questions with Yes/No options have been answered
  for (const [categoryId, category] of Object.entries(questionsData)) {
    category.questions.forEach((question, index) => {
      // Skip energy consumption and RE percentage questions which don't have Yes/No buttons
      if (question.text.includes("total energy consumption") || 
          question.text.includes("estimated On Site RE") || 
          question.text.includes("estimated Off Site RE")) {
        return;
      }
      
      // Check if this question has a points value (meaning it requires an answer)
      if (question.points !== undefined) {
        const key = `${categoryId}-${index}`;
        if (selectedAnswers[key] === undefined) {
          return false;
        }
      }
    });
  }
  return true;
};

const ChecklistPage = ({ 
  selectedClimateZone, 
  onSave, 
  builtUpArea, 
  initialData = null,
  isEditMode = false 
}) => {
  const [scores, setScores] = useState({});
  const [totalPoints, setTotalPoints] = useState(0);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showEnergyModal, setShowEnergyModal] = useState(false);
  const [energyData, setEnergyData] = useState(
    Array(12).fill({
      electricalConsumptionFromUtility: "",
      electricalConsumptionFromOnSiteSolarPV: "",
      electricalConsumptionFromOffSiteRenewable: "",
      totalConsumption: 0,
    })
  );
  const [energyPoints, setEnergyPoints] = useState(0);
  const climateZone = selectedClimateZone || "Composite";
  const [showCertificationPopup, setShowCertificationPopup] = useState(false);
  const [lastCertificationLevel, setLastCertificationLevel] = useState("");
  const [showRequiredQuestionsPopup, setShowRequiredQuestionsPopup] = useState(false);
  
  // Load initial data if in edit mode
  useEffect(() => {
    if (initialData && isEditMode) {
      loadInitialData();
    }
  }, [initialData, isEditMode]);
  
  const loadInitialData = () => {
    if (!initialData) return;
    
    // Set the total points
    setTotalPoints(initialData.totalPoints || 0);
    
    // Set up selected answers based on certification responses
    const newSelectedAnswers = {};
    const newScores = {};
    
    // Set energy data if available
    if (initialData.energyData && initialData.energyData.length > 0) {
      setEnergyData(initialData.energyData);
      
      // Calculate and set the correct EPI points based on the loaded energy data
      const totalConsumption = initialData.energyData.reduce(
        (sum, row) => sum + row.totalConsumption,
        0
      );
      
      let calculatedEpiPoints = 0;
      if (builtUpArea && totalConsumption > 0) {
        const areaInSqM = parseFloat(builtUpArea) * SQFT_TO_SQM;
        const epi = totalConsumption / areaInSqM;
        
        const ranges = climateZoneRanges[climateZone] || [];
        for (let range of ranges) {
          if (epi >= range.min && epi <= range.max) {
            calculatedEpiPoints = range.points;
            break;
          }
        }
      }
      
      // Set the EPI points based on actual calculation from the data
      setEnergyPoints(calculatedEpiPoints);
    }
    
    // Process certification responses
    if (initialData.certificationResponses && initialData.certificationResponses.length > 0) {
      // Create a map of questions to find their category and index
      const questionMap = {};
      Object.entries(questionsData).forEach(([categoryId, category]) => {
        category.questions.forEach((question, index) => {
          if (question.questionId) {
            questionMap[question.questionId] = { categoryId, index };
          }
        });
      });
      
      // First pass - handle non-energy questions
      let energySection3Points = 0;
      let hasProcessedRefrigerantQ = false;
      
      initialData.certificationResponses.forEach(response => {
        const questionInfo = questionMap[response.questionId];
        if (questionInfo) {
          const { categoryId, index } = questionInfo;
          const key = `${categoryId}-${index}`;
          
          // Set selected answer
          newSelectedAnswers[key] = response.answer;
          
          // Add points to category score if answer is true
          if (response.answer) {
            if (categoryId === '3') {
              // For energy section
              if (response.questionId === 32) {
                // Mandatory refrigerant question
                hasProcessedRefrigerantQ = true;
              } 
              else if (response.questionId === 34) {
                // Refrigerant gas question - worth 2 points
                energySection3Points += response.pointsAchieved || 2;
              }
              else if (response.questionId === 33) {
                // Energy consumption question - Use the actual points achieved
                // The UI display is set separately with setEnergyPoints above
                energySection3Points += response.pointsAchieved || 0;
              }
              else if (response.questionId === 35 || response.questionId === 36) {
                // RE questions
                energySection3Points += response.pointsAchieved || 0;
              }
            } else {
              // Normal questions in other sections
              newScores[categoryId] = (newScores[categoryId] || 0) + 
                (questionsData[categoryId].questions[index].points || 0);
            }
          }
        }
      });
      
      // Add the energy section points
      if (hasProcessedRefrigerantQ || energySection3Points > 0) {
        newScores['3'] = energySection3Points;
      }
    }
    
    setSelectedAnswers(newSelectedAnswers);
    setScores(newScores);
  };
  
  const calculateREPercentages = () => {
    const totalConsumption = energyData.reduce(
      (sum, row) => sum + row.totalConsumption,
      0
    );
    
    const totalOnSiteRE = energyData.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOnSiteSolarPV) || 0),
      0
    );
    
    const totalOffSiteRE = energyData.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOffSiteRenewable) || 0),
      0
    );

    if (totalConsumption === 0) return { onSiteRE: 0, offSiteRE: 0, onSitePoints: 0, offSitePoints: 0 };

    const onSiteREPercentage = (totalOnSiteRE / totalConsumption) * 100;
    const offSiteREPercentage = (totalOffSiteRE / totalConsumption) * 100;

    // Calculate On Site RE points
    let onSitePoints = 0;
    if (onSiteREPercentage > 50) onSitePoints = 7;
    else if (onSiteREPercentage > 30) onSitePoints = 6;
    else if (onSiteREPercentage > 20) onSitePoints = 5;
    else if (onSiteREPercentage > 15) onSitePoints = 4;
    else if (onSiteREPercentage > 10) onSitePoints = 3;
    else if (onSiteREPercentage > 5) onSitePoints = 2;

    // Calculate Off Site RE points
    let offSitePoints = 0;
    if (offSiteREPercentage === 100) offSitePoints = 7;
    else if (offSiteREPercentage > 90) offSitePoints = 6;
    else if (offSiteREPercentage > 80) offSitePoints = 5;
    else if (offSiteREPercentage > 70) offSitePoints = 4;
    else if (offSiteREPercentage > 60) offSitePoints = 3;
    else if (offSiteREPercentage > 50) offSitePoints = 2;

    return {
      onSiteRE: parseFloat(onSiteREPercentage.toFixed(2)),
      offSiteRE: parseFloat(offSiteREPercentage.toFixed(2)),
      onSitePoints,
      offSitePoints
    };
  };

  const calculateEnergyPointsFromData = (data) => {
    // Calculate EPI based points
    const totalConsumption = data.reduce(
      (sum, row) => sum + row.totalConsumption,
      0
    );
    
    let consumptionPoints = 0;
    if (builtUpArea && totalConsumption > 0) {
      const areaInSqM = parseFloat(builtUpArea) * SQFT_TO_SQM;
      const epi = totalConsumption / areaInSqM; // This gives kWh/m²/year

      const ranges = climateZoneRanges[climateZone] || [];
      for (const range of ranges) {
        if (epi >= range.min && epi <= range.max) {
          consumptionPoints = range.points;
          break;
        }
      }
    }

    // Calculate RE points for On Site and Off Site separately
    const totalOnSiteRE = data.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOnSiteSolarPV) || 0),
      0
    );
    
    const totalOffSiteRE = data.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOffSiteRenewable) || 0),
      0
    );
    
    // Calculate On Site RE points
    const onSiteREPercentage = totalConsumption > 0 ? (totalOnSiteRE / totalConsumption) * 100 : 0;
    let onSitePoints = 0;
    if (onSiteREPercentage > 50) onSitePoints = 7;
    else if (onSiteREPercentage > 30) onSitePoints = 6;
    else if (onSiteREPercentage > 20) onSitePoints = 5;
    else if (onSiteREPercentage > 15) onSitePoints = 4;
    else if (onSiteREPercentage > 10) onSitePoints = 3;
    else if (onSiteREPercentage > 5) onSitePoints = 2;
    
    // Calculate Off Site RE points
    const offSiteREPercentage = totalConsumption > 0 ? (totalOffSiteRE / totalConsumption) * 100 : 0;
    let offSitePoints = 0;
    if (offSiteREPercentage === 100) offSitePoints = 7;
    else if (offSiteREPercentage > 90) offSitePoints = 6;
    else if (offSiteREPercentage > 80) offSitePoints = 5;
    else if (offSiteREPercentage > 70) offSitePoints = 4;
    else if (offSiteREPercentage > 60) offSitePoints = 3;
    else if (offSiteREPercentage > 50) offSitePoints = 2;
    
    // Get the refrigerant question points
    const refrigerantPoints = selectedAnswers['3-1'] === true ? 2 : 0;
    
    // Calculate total energy points with cap
    const calculatedPoints = consumptionPoints + onSitePoints + offSitePoints + refrigerantPoints;
    const maxEnergyPoints = questionsData[3].maxPoints; 
    const totalEnergyPoints = Math.min(calculatedPoints, maxEnergyPoints);
    
    // Update energy points state but only for the EPI-based points
    setEnergyPoints(consumptionPoints);
    
    // Return the capped total energy points
    return totalEnergyPoints;
  };

  const calculateEnergyPoints = () => {
    // Temporarily store the current certification level to prevent popup
    const prevCertificationLevel = lastCertificationLevel;
    
    // Calculate points as usual
    const totalConsumption = energyData.reduce(
      (sum, row) => sum + row.totalConsumption,
      0
    );
    
    let consumptionPoints = 0;
    if (builtUpArea && totalConsumption > 0) {
      const areaInSqM = parseFloat(builtUpArea) * SQFT_TO_SQM;
      const epi = totalConsumption / areaInSqM; // kWh/m²/year
      
      const ranges = climateZoneRanges[climateZone] || [];
      for (const range of ranges) {
        if (epi >= range.min && epi <= range.max) {
          consumptionPoints = range.points;
          break;
        }
      }
    }
    
    // Calculate On-Site RE points
    const totalOnSiteRE = energyData.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOnSiteSolarPV) || 0),
      0
    );
    const onSiteREPercentage = totalConsumption > 0 ? (totalOnSiteRE / totalConsumption) * 100 : 0;
    
    let onSitePoints = 0;
    if (onSiteREPercentage > 50) onSitePoints = 7;
    else if (onSiteREPercentage > 30) onSitePoints = 6;
    else if (onSiteREPercentage > 20) onSitePoints = 5;
    else if (onSiteREPercentage > 15) onSitePoints = 4;
    else if (onSiteREPercentage > 10) onSitePoints = 3;
    else if (onSiteREPercentage > 5) onSitePoints = 2;
    
    // Calculate Off-Site RE points
    const totalOffSiteRE = energyData.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOffSiteRenewable) || 0),
      0
    );
    const offSiteREPercentage = totalConsumption > 0 ? (totalOffSiteRE / totalConsumption) * 100 : 0;
    
    let offSitePoints = 0;
    if (offSiteREPercentage === 100) offSitePoints = 7;
    else if (offSiteREPercentage > 90) offSitePoints = 6;
    else if (offSiteREPercentage > 80) offSitePoints = 5;
    else if (offSiteREPercentage > 70) offSitePoints = 4;
    else if (offSiteREPercentage > 60) offSitePoints = 3;
    else if (offSiteREPercentage > 50) offSitePoints = 2;
    
    // Get the refrigerant question points
    const refrigerantPoints = selectedAnswers['3-1'] === true ? 2 : 0;
    
    // Calculate total energy points (with cap at maxPoints)
    const calculatedPoints = consumptionPoints + onSitePoints + offSitePoints + refrigerantPoints;
    const maxEnergyPoints = questionsData[3].maxPoints; // This is 30 based on the data
    const totalEnergyPoints = Math.min(calculatedPoints, maxEnergyPoints);
    
    // Update UI display for EPI-based points only
    setEnergyPoints(consumptionPoints);
    
    // Prevent certification popup by preserving last certification level
    setLastCertificationLevel(prevCertificationLevel);
    
    // Update scores with the capped total energy points
    setScores((prevScores) => {
      const newScores = { ...prevScores, 3: totalEnergyPoints };
      
      // Calculate new total score
      const newTotal = Object.values(newScores).reduce(
        (sum, value) => sum + (value || 0),
        0
      );
      
      // Update total points 
      setTotalPoints(newTotal);
      
      return newScores;
    });
    
    // Close the energy modal
    setShowEnergyModal(false);
  };

  useEffect(() => {
    const currentLevel = getCertificationLevel(totalPoints);
    if (currentLevel !== lastCertificationLevel && totalPoints >= 40) {
      setShowCertificationPopup(true);
      setLastCertificationLevel(currentLevel);
    }
  }, [totalPoints, lastCertificationLevel]);


  const getCertificationLevel = (points) => {
    // Check mandatory questions first
    if (!areMandatoryQuestionsAnswered(selectedAnswers)) {
      return "Not Certified";
    }
   
    if (points < 40) return "Not Certified";
    if (points <= 50) return "Certified";
    if (points <= 60) return "Silver";
    if (points <= 75) return "Gold";
    if (points <= 100) return "Platinum";
    return "Error";
  };

  const getCertificationMessage = (points) => {
    // Check mandatory questions first
    if (!areMandatoryQuestionsAnswered(selectedAnswers)) {
      return "Not Certified - Mandatory criteria is not met";
    }
   
    if (points >= 75) {
      return "Congratulations, you qualify for Platinum Certification for green building. Contact RHG ASO for details.";
    } else if (points >= 60) {
      return "Congratulations, you qualify for Gold Certification for green building. Contact RHG ASO for details.";
    } else if (points >= 50) {
      return "Congratulations, you qualify for Silver Certification for green building. Contact RHG ASO for details.";
    } else if (points >= 40) {
      return "Congratulations, you qualify for green building certification. Contact RHG ASO for details.";
    }
    return "Not Certified - Minimum score requirement not met";
  };

  const handleCredibilityCheck = () => {
    // handleSave();
    setShowCertificationPopup(true);
  };

  const handleSelection = (categoryId, questionIndex, points, isYes) => {
    const key = `${categoryId}-${questionIndex}`;
    const newScores = { ...scores };
    const newAnswers = { ...selectedAnswers };

    // Check if the same option is clicked again
    if (newAnswers[key] === isYes) return;

    // If switching from Yes → No, remove points
    if (!isYes && newAnswers[key] === true) {
      newScores[categoryId] = (newScores[categoryId] || 0) - points;
    }
    // If switching from No → Yes, add points
    else if (isYes) {
      newScores[categoryId] = (newScores[categoryId] || 0) + points;
    }

    newAnswers[key] = isYes;
    setSelectedAnswers(newAnswers);
    setScores(newScores);

    // Calculate total including energy points
    const newTotal = Object.values(newScores).reduce(
      (sum, value) => sum + (value || 0),
      0
    );
    setTotalPoints(newTotal);
    setShowEnergyModal(false);
  };

  const handleEnergyInputChange = (index, field, value) => {
    const updatedData = [...energyData];
    updatedData[index] = { ...updatedData[index], [field]: value };
    updatedData[index].totalConsumption =
      (parseFloat(updatedData[index].electricalConsumptionFromUtility) || 0) +
      (parseFloat(updatedData[index].electricalConsumptionFromOnSiteSolarPV) ||
        0) +
      (parseFloat(
        updatedData[index].electricalConsumptionFromOffSiteRenewable
      ) || 0);
    setEnergyData(updatedData);
  };

  // Handle key press for Excel-like navigation
  const handleEnergyInputKeyDown = (e, rowIndex, colIndex) => {
    const columns = ["electricalConsumptionFromUtility", "electricalConsumptionFromOnSiteSolarPV", "electricalConsumptionFromOffSiteRenewable"];
    
    // Prevent default arrow key behavior (which changes the value)
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
      e.preventDefault();
      
      let nextRowIndex = rowIndex;
      let nextColIndex = colIndex;
      
      // Navigate based on arrow key
      switch (e.key) {
        case "ArrowUp":
          nextRowIndex = Math.max(0, rowIndex - 1);
          break;
        case "ArrowDown":
          nextRowIndex = Math.min(11, rowIndex + 1);
          break;
        case "ArrowLeft":
          nextColIndex = Math.max(0, colIndex - 1);
          break;
        case "ArrowRight":
          nextColIndex = Math.min(2, colIndex + 1);
          break;
        default:
          break;
      }
      
      // Focus the next input if navigation changed
      if (nextRowIndex !== rowIndex || nextColIndex !== colIndex) {
        const nextInput = document.querySelector(
          `.energy-input-${nextRowIndex}-${nextColIndex}`
        );
        if (nextInput) nextInput.focus();
      }
    }
  };

  // This new function will recalculate total points whenever needed
  const updateTotalPoints = () => {
    // Get the current scores and calculate the total
    const newTotal = Object.values(scores).reduce(
      (sum, value) => sum + (value || 0),
      0
    );
    setTotalPoints(newTotal);
  };

  // Add an effect to recalculate total points whenever scores change
  useEffect(() => {
    updateTotalPoints();
  }, [scores]);

  const formatCertificationResponses = (selectedAnswers, energyData) => {
    const responses = [];
    
    // Calculate energy points for Q33, Q35, and Q36
    const totalConsumption = energyData.reduce(
      (sum, row) => sum + row.totalConsumption,
      0
    );
    
    // Calculate EPI-based points for Q33
    let epiCalculatedPoints = 0;
    if (builtUpArea && totalConsumption > 0) {
      const areaInSqM = parseFloat(builtUpArea) * SQFT_TO_SQM;
      const epi = totalConsumption / areaInSqM; // This gives kWh/m²/year
      
      const ranges = climateZoneRanges[climateZone] || [];
      for (const range of ranges) {
        if (epi >= range.min && epi <= range.max) {
          epiCalculatedPoints = range.points;
          break;
        }
      }
    }
    
    // Calculate RE percentage for On Site and Off Site separately
    const totalOnSiteRE = energyData.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOnSiteSolarPV) || 0),
      0
    );
    const totalOffSiteRE = energyData.reduce(
      (sum, row) => sum + (parseFloat(row.electricalConsumptionFromOffSiteRenewable) || 0),
      0
    );
    
    // Calculate On Site RE percentage and points for Q36
    const onSiteREPercentage = totalConsumption > 0 ? (totalOnSiteRE / totalConsumption) * 100 : 0;
    let onSitePoints = 0;
    if (onSiteREPercentage > 50) onSitePoints = 7;
    else if (onSiteREPercentage > 30) onSitePoints = 6;
    else if (onSiteREPercentage > 20) onSitePoints = 5;
    else if (onSiteREPercentage > 15) onSitePoints = 4;
    else if (onSiteREPercentage > 10) onSitePoints = 3;
    else if (onSiteREPercentage > 5) onSitePoints = 2;
    
    // Calculate Off Site RE percentage and points for Q35
    const offSiteREPercentage = totalConsumption > 0 ? (totalOffSiteRE / totalConsumption) * 100 : 0;
    let offSitePoints = 0;
    if (offSiteREPercentage === 100) offSitePoints = 7;
    else if (offSiteREPercentage > 90) offSitePoints = 6;
    else if (offSiteREPercentage > 80) offSitePoints = 5;
    else if (offSiteREPercentage > 70) offSitePoints = 4;
    else if (offSiteREPercentage > 60) offSitePoints = 3;
    else if (offSiteREPercentage > 50) offSitePoints = 2;
    
    // Process all questions including energy questions
    Object.entries(questionsData).forEach(([categoryId, category]) => {
      category.questions.forEach((question, index) => {
        const questionIdentifier = question.questionId || question.id;
        
        if (questionIdentifier) {
          // Special handling for energy consumption question (Q33)
          if (questionIdentifier === 33) {
            const hasEnergyData = energyData.some(row => row.totalConsumption > 0);
            responses.push({
              questionId: questionIdentifier,
              answer: true, 
              pointsAchieved: hasEnergyData ? epiCalculatedPoints : 0
            });
          }
          // Off Site RE question (Q35)
          else if (questionIdentifier === 35) {
            const hasConsumption = totalConsumption > 0;
            responses.push({
              questionId: questionIdentifier,
              answer: true,
              pointsAchieved: hasConsumption ? offSitePoints : 0
            });
          }
          // On Site RE question (Q36)
          else if (questionIdentifier === 36) {
            const hasConsumption = totalConsumption > 0;
            responses.push({
              questionId: questionIdentifier,
              answer: true,
              pointsAchieved: hasConsumption ? onSitePoints : 0
            });
          }
          // Refrigerant gases question (Q34)
          else if (questionIdentifier === 34) {
            const isAnswered = selectedAnswers[`${categoryId}-${index}`] !== undefined;
            responses.push({
              questionId: questionIdentifier,
              answer: isAnswered ? selectedAnswers[`${categoryId}-${index}`] : false,
              pointsAchieved: selectedAnswers[`${categoryId}-${index}`] === true ? question.points : 0
            });
          }
          // Normal Yes/No questions
          else if (selectedAnswers[`${categoryId}-${index}`] !== undefined) {
            responses.push({
              questionId: questionIdentifier,
              answer: selectedAnswers[`${categoryId}-${index}`],
              pointsAchieved: selectedAnswers[`${categoryId}-${index}`] ? question.points : 0
            });
          }
        }
      });
    });

    return responses;
  };

  const handleSave = () => {
    const saveButton = document.querySelector(".save-btn");
    if (saveButton) saveButton.disabled = true;

    // Check if all questions are answered
    if (!areAllQuestionsAnswered(selectedAnswers)) {
      setShowRequiredQuestionsPopup(true);
      if (saveButton) saveButton.disabled = false;
      return;
    }

    // Check if mandatory questions are answered
    if (!areMandatoryQuestionsAnswered(selectedAnswers)) {
      setShowCertificationPopup(true);
      if (saveButton) saveButton.disabled = false;
      return;
    }

    try {
      const certificationResponses = formatCertificationResponses(selectedAnswers, energyData);
      
      onSave({
        totalPoints,
        categoryScores: scores,
        selectedAnswers,
        energyData,
        energyPoints,
        CalculatedCertificationLevel: getCertificationLevel(totalPoints),
        certificationResponses
      });
    } catch (error) {
      console.error("Error in save handler:", error);
    } finally {
      if (saveButton) saveButton.disabled = false;
    }
  };

  return (
    <div className="checklist-container">
    
      <div className="total-points">
        <span>TOTAL POINTS</span>
        <span className="points-box">{totalPoints}/100</span>
      </div>

      <div className="mandatory-legend">
        <span className="mandatory-indicator">*</span>
        <span>Indicates mandatory questions that must be answered <b>'Yes'</b> for certification</span>
      </div>

      {isEditMode && (
        <div className="edit-mode-banner">
          <span>You are currently editing an existing hotel certification. Make your changes and click Save to update.</span>
        </div>
      )}
     
      {Object.entries(questionsData).map(([id, category]) => (
        <div key={id} className="category">
          <div
            className={`category-header ${
              activeCategory === id ? "active" : ""
            }`}
            onClick={() => setActiveCategory(activeCategory === id ? null : id)}
          >
            <span>
              {category.icon} {category.name}
            </span>
            <div className="category-header-right">
              <span className="category-score">
                {scores[id] || 0}/{category.maxPoints}
              </span>
              <span className="category-arrow">▼</span>
            </div>
          </div>
          <div
            className={`category-content ${
              activeCategory === id ? "active" : ""
            }`}
          >
            <div className="scrollable-container">
              {category.questions.map((q, index) => (
                <div key={index} className="question">
                  <p>
                    {q.text}
                    {mandatoryQuestions[id]?.includes(index) && (
                      <span className="mandatory-indicator" style={{ color: 'red', marginLeft: '5px' }}>*</span>
                    )}
                  </p>
                  {q.text.includes("total energy consumption") ? (
                    <div className="energy-button-container">
                      <button
                        className="energy-button"
                        onClick={() => setShowEnergyModal(true)}
                      >
                        Click Here
                      </button>
                      {energyPoints > 0 && (
                        <span className="points-indicator">
                          {" "}
                          +{energyPoints} points
                        </span>
                      )}
                    </div>
                  ) : q.text.includes("estimated Off Site Renewable Energy") ? (
                    <div className="percentage-display">
                      {calculateREPercentages().offSiteRE}%
                      {energyData.some((row) => row.totalConsumption > 0) && (
                        <span className="points-indicator">+{calculateREPercentages().offSitePoints} points</span>
                      )}
                    </div>
                  ) : q.text.includes("estimated On Site Renewable Energy") ? (
                    <div className="percentage-display">
                      {calculateREPercentages().onSiteRE}%
                      {energyData.some((row) => row.totalConsumption > 0) && (
                        <span className="points-indicator">+{calculateREPercentages().onSitePoints} points</span>
                      )}
                    </div>
                  ) : q.questionId ? (
                    <div className="answer-buttons">
                      <button
                        className={`yes ${
                          selectedAnswers[`${id}-${index}`] === true
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          handleSelection(id, index, q.points, true)
                        }
                      >
                        Yes
                      </button>
                      <button
                        className={`no ${
                          selectedAnswers[`${id}-${index}`] === false
                            ? "selected"
                            : ""
                        }`}
                        onClick={() =>
                          handleSelection(id, index, q.points, false)
                        }
                      >
                        No
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      <div className="buttons">
        <button className="save-btn" onClick={handleSave}>
          Save
        </button>
        <button className="credibility-btn" onClick={handleCredibilityCheck}>
          Check Credibility
          <span>↗️</span>
        </button>
      </div>

      {showEnergyModal && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>Enter Energy Consumption Data</h2>
              <div className="energy-points-display">
                <div>
                  Climate Zone: <strong>{climateZone}</strong>
                </div>
                <div>
                  Energy Consumption Points: <strong>{energyPoints}/14</strong>
                </div>
                <div>
                  Built-up Area:{" "}
                  <strong>
                    {(parseFloat(builtUpArea) * SQFT_TO_SQM).toFixed(2)} m²
                  </strong>
                </div>
              </div>
            </div>

            <div className="table-container">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Electrical Consumption from Utility (kWh)</th>
                      <th>
                        Electrical Consumption from On Site Solar PV System (kWh)
                      </th>
                      <th>
                        Electrical Consumption from Off Site Renewable System
                        (kWh)
                      </th>
                      <th>Total Consumption (kWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {energyData.map((row, index) => (
                      <tr key={index}>
                        <td>
                          {new Date(0, index).toLocaleString("default", {
                            month: "long",
                          })}
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`energy-input-${index}-0`}
                            value={row.electricalConsumptionFromUtility}
                            onChange={(e) =>
                              handleEnergyInputChange(
                                index,
                                "electricalConsumptionFromUtility",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => handleEnergyInputKeyDown(e, index, 0)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`energy-input-${index}-1`}
                            value={row.electricalConsumptionFromOnSiteSolarPV}
                            onChange={(e) =>
                              handleEnergyInputChange(
                                index,
                                "electricalConsumptionFromOnSiteSolarPV",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => handleEnergyInputKeyDown(e, index, 1)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`energy-input-${index}-2`}
                            value={row.electricalConsumptionFromOffSiteRenewable}
                            onChange={(e) =>
                              handleEnergyInputChange(
                                index,
                                "electricalConsumptionFromOffSiteRenewable",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => handleEnergyInputKeyDown(e, index, 2)}
                          />
                        </td>
                        <td>{row.totalConsumption}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td>
                        <b>Total</b>
                      </td>
                      <td>
                        <b>
                          {energyData.reduce(
                            (sum, row) =>
                              sum +
                              (parseFloat(row.electricalConsumptionFromUtility) ||
                                0),
                            0
                          )}
                        </b>
                      </td>
                      <td>
                        <b>
                          {energyData.reduce(
                            (sum, row) =>
                              sum +
                              (parseFloat(
                                row.electricalConsumptionFromOnSiteSolarPV
                              ) || 0),
                            0
                          )}
                        </b>
                      </td>
                      <td>
                        <b>
                          {energyData.reduce(
                            (sum, row) =>
                              sum +
                              (parseFloat(
                                row.electricalConsumptionFromOffSiteRenewable
                              ) || 0),
                            0
                          )}
                        </b>
                      </td>
                      <td>
                        <b>
                          {energyData.reduce(
                            (sum, row) => sum + row.totalConsumption,
                            0
                          )}
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={calculateEnergyPoints}>Save Data</button>
              <button onClick={() => setShowEnergyModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showCertificationPopup && (
        <>
          <div
            className="popup-overlay"
            onClick={() => setShowCertificationPopup(false)}
          />
          <div className="certification-popup">
           
            <div
              className={`certification-status ${getCertificationLevel(
                totalPoints
              )
                .toLowerCase()
                .replace(" ", "-")}`}
            >
             <p>{getCertificationMessage(totalPoints)}</p>
            </div>
            <p>Total Points: {totalPoints}/100</p>
            
            <button
              className="close-popup"
              onClick={() => setShowCertificationPopup(false)}
            >
              Continue
            </button>
          </div>
        </>
      )}

      {showRequiredQuestionsPopup && (
        <>
          <div
            className="popup-overlay"
            onClick={() => setShowRequiredQuestionsPopup(false)}
          />
          <div className="certification-popup required-questions-popup">
            <div className="certification-status not-certified">
              <p>Please answer all questions before saving.</p>
              <p>Each question requires either a "Yes" or "No" response.</p>
            </div>
            <button
              className="close-popup"
              onClick={() => setShowRequiredQuestionsPopup(false)}
            >
              Continue
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChecklistPage;
